import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { admin as api } from "@/lib/api/admin";
import type {
  AdminUserDetail,
  AdminUserFilter,
  AdminUserPatch,
  AdminUserRevenue,
  AdminUserSummary,
  InviteUserInput,
} from "@/lib/api/admin";
import { ApiError } from "@/lib/api/transport";
import { useAuth } from "./useAuth";

// Re-export so admin components can pull types from the hook layer
// rather than reaching into @/lib/api directly (no-restricted-imports).
export type {
  AdminUserDetail,
  AdminUserFilter,
  AdminUserPatch,
  AdminUserRevenue,
  AdminUserStats,
  AdminUserSummary,
  AdminAccessGrant,
  InviteDuration,
  InviteUserInput,
} from "@/lib/api/admin";

// Distinct invite-error tag the InviteModal renders specially.
export type InviteUserError = "email_taken" | "unknown";

function bodyCode(body: unknown): string | null {
  return typeof body === "object" &&
    body !== null &&
    "code" in body &&
    typeof (body as { code?: unknown }).code === "string"
    ? (body as { code: string }).code
    : null;
}

export type UpdateUserError = "pin_taken" | "unknown";

export class UpdateUserFailed extends Error {
  readonly code: UpdateUserError;
  constructor(code: UpdateUserError) {
    super(code);
    this.code = code;
  }
}

export class InviteUserFailed extends Error {
  readonly code: InviteUserError;
  constructor(code: InviteUserError) {
    super(code);
    this.code = code;
  }
}

export const DEFAULT_ADMIN_USER_LIMIT = 200;

export type AdminUsersStatus = "idle" | "loading" | "ready" | "error";

export interface AdminUserMembershipCounts {
  readonly trial: number;
  readonly subscribed: number;
  readonly expired: number;
  readonly permanent: number;
}

export interface AdminUsersContextValue {
  status: AdminUsersStatus;
  users: readonly AdminUserSummary[];
  userCount: number;
  membershipCounts: AdminUserMembershipCounts;
  revenue: AdminUserRevenue;
  filter: AdminUserFilter;
  error: Error | null;
  reload: (filter?: AdminUserFilter) => Promise<void>;
  setFilter: (filter: AdminUserFilter) => void;
  fetchDetail: (id: number) => Promise<AdminUserDetail>;
  update: (id: number, patch: AdminUserPatch) => Promise<AdminUserDetail>;
  remove: (id: number) => Promise<void>;
  resendPin: (id: number) => Promise<void>;
  searchUsers: (
    q: string,
    limit?: number,
  ) => Promise<readonly AdminUserSummary[]>;
  inviteUser: (input: InviteUserInput) => Promise<AdminUserSummary>;
}

const AdminUsersContext = createContext<AdminUsersContextValue>(null!);

export function useAdminUsers() {
  return useContext(AdminUsersContext);
}

function useAdminUsersProvider(): AdminUsersContextValue {
  const { user, status: authStatus } = useAuth();
  const [status, setStatus] = useState<AdminUsersStatus>("idle");
  const [items, setItems] = useState<readonly AdminUserSummary[]>([]);
  const [filter, setFilterState] = useState<AdminUserFilter>({
    limit: DEFAULT_ADMIN_USER_LIMIT,
  });
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<AdminUserMembershipCounts>({
    trial: 0,
    subscribed: 0,
    expired: 0,
    permanent: 0,
  });
  const [revenue, setRevenue] = useState<AdminUserRevenue>({
    paying_users: 0,
    total_cents: 0,
  });
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    (next?: AdminUserFilter) =>
      api
        .listAdminUsers(next ?? filter)
        .then(({ users, total, membership_counts, revenue }) => {
          setItems(users);
          setTotal(total);
          setCounts(membership_counts);
          setRevenue(revenue);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [filter],
  );

  const setFilter = useCallback(
    (next: AdminUserFilter) => {
      setFilterState(next);
      void reload(next);
    },
    [reload],
  );

  useEffect(() => {
    if (authStatus !== "authenticated" || !user?.admin) return;
    void reload();
    // We intentionally only kick a load on auth flip — `reload` is
    // stable per-filter and `setFilter` re-runs it on user input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, user?.admin]);

  const fetchDetail = useCallback(async (id: number) => {
    const { user: detail } = await api.getAdminUser(id);
    return detail;
  }, []);

  const update = useCallback(async (id: number, patch: AdminUserPatch) => {
    const { user: detail } = await api
      .updateAdminUser(id, patch)
      .catch((err: unknown) => {
        throw new UpdateUserFailed(
          err instanceof ApiError && bodyCode(err.body) === "pin_taken"
            ? "pin_taken"
            : "unknown",
        );
      });
    setItems((prev) =>
      prev.map((u) =>
        u.id === id
          ? {
              ...u,
              name: detail.name,
              email: detail.email,
              pin: detail.pin,
              tier: detail.tier,
              active: detail.active,
              admin: detail.admin,
              expires_at: detail.expires_at,
              paid_at: detail.paid_at,
            }
          : u,
      ),
    );
    return detail;
  }, []);

  const remove = useCallback(async (id: number) => {
    await api.deleteAdminUser(id);
    setItems((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const searchUsers = useCallback(async (q: string, limit = 20) => {
    const { users } = await api.listAdminUsers({ q, limit });
    return users;
  }, []);

  const resendPin = useCallback(async (id: number) => {
    await api.resendAdminUserPin(id);
  }, []);

  const inviteUser = useCallback(async (input: InviteUserInput) => {
    try {
      const { user } = await api.inviteAdminUser(input);
      setItems((prev) => [user, ...prev]);
      return user;
    } catch (err) {
      if (err instanceof ApiError) {
        const code =
          typeof err.body === "object" &&
          err.body !== null &&
          "error" in err.body &&
          typeof (err.body as { error?: unknown }).error === "string"
            ? (err.body as { error: string }).error
            : null;
        throw new InviteUserFailed(
          code === "email_taken" ? "email_taken" : "unknown",
        );
      }
      throw new InviteUserFailed("unknown");
    }
  }, []);

  const userCount = total;
  const membershipCounts = counts;

  return useMemo(
    () => ({
      status,
      users: items,
      userCount,
      membershipCounts,
      revenue,
      filter,
      error,
      reload,
      setFilter,
      fetchDetail,
      update,
      remove,
      resendPin,
      searchUsers,
      inviteUser,
    }),
    [
      status,
      items,
      userCount,
      membershipCounts,
      revenue,
      filter,
      error,
      reload,
      setFilter,
      fetchDetail,
      update,
      remove,
      resendPin,
      searchUsers,
      inviteUser,
    ],
  );
}

export function AdminUsersProvider({ children }: { children: ReactNode }) {
  const value = useAdminUsersProvider();
  return (
    <AdminUsersContext.Provider value={value}>
      {children}
    </AdminUsersContext.Provider>
  );
}
